import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
} from '@angular/core';
import { HeaderService } from '@padspin/ui-header';
import { MetadataService } from '@padspin/ui-metadata';
import {
  MARKETING_NAV,
  MarketingCitiesSectionComponent,
  MarketingTestimonialsSectionComponent,
} from '@padspin/ui-marketing-shared';
import { MarketingLandlordHeroComponent } from './landlord-hero.component';
import { MarketingLandlordHowItWorksComponent } from './landlord-how-it-works.component';
import { MarketingLandlordComparisonComponent } from './landlord-comparison.component';
import { MarketingLandlordBenefitsComponent } from './landlord-benefits.component';
import { MarketingLandlordCtaComponent } from './landlord-cta.component';

const PADSPIN_LOGO_URL =
  'https://storage.googleapis.com/padspinv2.appspot.com/web/padspin-square-logo.png';

const PADSPIN_ORGANIZATION_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Padspin',
  url: 'https://padspin.com/',
  logo: PADSPIN_LOGO_URL,
  description:
    'Padspin leases homes and apartments for landlords for a flat $800 per rented unit, and lists rentals with no broker fee.',
  email: 'support@padspin.com',
  sameAs: [
    'https://www.facebook.com/padspin/',
    'https://www.instagram.com/getpadspin',
    'https://www.linkedin.com/company/padspin',
    'https://www.tiktok.com/@padspin',
    'https://www.youtube.com/@getpadspin',
  ],
};

@Component({
  selector: 'padspin-marketing-landlord-page',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MarketingLandlordHeroComponent,
    MarketingLandlordHowItWorksComponent,
    MarketingLandlordComparisonComponent,
    MarketingLandlordBenefitsComponent,
    MarketingCitiesSectionComponent,
    MarketingTestimonialsSectionComponent,
    MarketingLandlordCtaComponent,
  ],
  template: `
    <div class="bg-surface-50 dark:bg-surface-950 flex flex-col">
      <padspin-marketing-landlord-hero />
      @defer (on viewport; hydrate on viewport) {
        <padspin-marketing-landlord-how-it-works />
      } @placeholder {
        <div class="min-h-[400px]"></div>
      }
      @defer (on viewport; hydrate on viewport) {
        <padspin-marketing-landlord-comparison />
      } @placeholder {
        <div class="min-h-[400px]"></div>
      }
      @defer (on viewport; hydrate on viewport) {
        <padspin-marketing-landlord-benefits />
      } @placeholder {
        <div class="min-h-[400px]"></div>
      }
      @defer (on viewport; hydrate on viewport) {
        <padspin-marketing-cities-section />
      } @placeholder {
        <div class="min-h-[400px]"></div>
      }
      @defer (on viewport; hydrate on viewport) {
        <padspin-marketing-testimonials-section />
      } @placeholder {
        <div class="min-h-[400px]"></div>
      }
      @defer (on viewport; hydrate on viewport) {
        <padspin-marketing-landlord-cta />
      } @placeholder {
        <div class="min-h-[400px]"></div>
      }
    </div>
  `,
})
export class MarketingLandlordPageComponent implements OnInit {
  private readonly header = inject(HeaderService);
  private readonly mds = inject(MetadataService);

  ngOnInit(): void {
    this.header.setType({ type: 'filled', background: 'white' });
    this.header.setMenuItems([...MARKETING_NAV]);
    this.header.setAltLandingLink({
      label: "I'm a Renter",
      routerLink: '/rent',
    });
    this.mds.setRequiredTags({
      title: 'Padspin — AI-Powered Home & Apartment Leasing',
      type: 'website',
      description:
        'Get a highly qualified renter in just days. No vacancies, no brokers, no nonsense. Flat $800 per rented unit.',
      url: 'https://padspin.com/',
    });
    this.mds.setOgImage(PADSPIN_LOGO_URL);
    this.mds.setCanonical('https://padspin.com/');
    this.mds.setJsonLd(PADSPIN_ORGANIZATION_JSON_LD);
  }
}
